import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

export interface ExamResult {
  id: string;
  name: string;
  date: string;
  correct: number;
  wrong: number;
  blank: number;
  net: number;
  totalQuestions: number;
  rank: string;
}

export interface UpcomingExam {
  id: string;
  name: string;
  date: string;
  time: string;
  duration: string;
  subject: string;
}

export interface SubjectPerformance {
  subject: string;
  net: number;
  maxNet: number;
  color: string;
}

export interface ExamQuestion {
  id: number;
  lessonName: string;
  topicName: string;
  difficulty: string;
  questionText: string;
  options: string[];
  correctAnswer: string;
}

const OPTION_KEYS = ["option_a", "option_b", "option_c", "option_d", "option_e"];

/**
 * Bir denemeye ait soruları soru havuzundan (question_bank) getirir.
 * Denemenin dersine (subject) göre filtreler.
 */
export function useExamQuestions(subject: string, gradeLevel?: string | null) {
  return useDataQuery<ExamQuestion[]>(async () => {
    const dataClient = getDataClient();
    const columns =
      "id, lesson_name, topic_name, difficulty, question_text, option_a, option_b, option_c, option_d, option_e, correct_answer";
    let query = dataClient
      .from("question_bank")
      .select(columns)
      .eq("verified", true)
      .eq("lesson_name", subject);
    if (gradeLevel) {
      query = query.eq("grade_level", gradeLevel);
    }
    const { data, error } = await query.limit(20);
    if (error) throw error;
    return (data ?? []).map((q: Record<string, unknown>) => {
      const options = OPTION_KEYS
        .map((k) => q[k])
        .filter((v): v is string => typeof v === "string" && v !== "");
      return {
        id: Number(q.id),
        lessonName: String(q.lesson_name ?? ""),
        topicName: String(q.topic_name ?? ""),
        difficulty: String(q.difficulty ?? ""),
        questionText: String(q.question_text ?? ""),
        options,
        correctAnswer: String(q.correct_answer ?? "").toUpperCase(),
      };
    });
  }, [subject, gradeLevel]);
}

export interface ExamAttemptInput {
  examName: string;
  correct: number;
  wrong: number;
  blank: number;
  net: number;
  totalQuestions: number;
}

/**
 * Deneme sonucunu exam_attempts tablosuna kaydeder.
 */
export async function submitExamAttempt(input: ExamAttemptInput): Promise<boolean> {
  try {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return false;
    const { error } = await dataClient.from("exam_attempts").insert({
      user_id: userId,
      exam_name: input.examName,
      correct_count: input.correct,
      wrong_count: input.wrong,
      blank_count: input.blank,
      net: input.net,
      total_questions: input.totalQuestions,
      taken_on: new Date().toISOString().slice(0, 10),
    });
    return !error;
  } catch {
    return false;
  }
}

function fullDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

export function useExamResults() {
  return useDataQuery<ExamResult[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("exam_attempts")
      .select("*")
      .eq("user_id", userId)
      .order("taken_on", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((e: { id: string; exam_name: string; correct_count: number; wrong_count: number; blank_count: number; net: number; taken_on: string; rank: string | null; total_questions: number | null }) => ({
      id: e.id,
      name: e.exam_name,
      date: fullDate(e.taken_on),
      correct: e.correct_count ?? 0,
      wrong: e.wrong_count ?? 0,
      blank: e.blank_count ?? 0,
      net: Number(e.net ?? 0),
      totalQuestions: e.total_questions ?? 0,
      rank: e.rank ?? "",
    }));
  });
}

export function useUpcomingExams() {
  return useDataQuery<UpcomingExam[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.from("upcoming_exams").select("*").order("sort_order");
    if (error) throw error;
    return (data ?? []).map((e: { id: string; name: string; exam_date: string; exam_time: string; duration: string; subject: string }) => ({
      id: e.id,
      name: e.name,
      date: e.exam_date,
      time: e.exam_time,
      duration: e.duration,
      subject: e.subject,
    }));
  });
}

export function useSubjectPerformance() {
  return useDataQuery<SubjectPerformance[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient.from("subject_performance").select("*").eq("user_id", userId);
    if (error) throw error;
    return (data ?? []).map((s: { subject: string; net: number; max_net: number; color: string | null }) => ({
      subject: s.subject,
      net: Number(s.net ?? 0),
      maxNet: Number(s.max_net ?? 0),
      color: s.color ?? "bg-primary-500",
    }));
  });
}